import { create } from 'zustand';
import { getChatbotConfigSection, updateChatbotConfigSection } from '../utils/chatbotConfigAccess';

interface KnowledgeItem {
  id: string;
  name: string;
  type: 'file' | 'text' | 'url';
  content?: string;
  url?: string;
  metadata?: Record<string, any>;
}

interface KnowledgeInput {
  type: 'file' | 'text' | 'url';
  name: string;
  content?: string;
  url?: string;
  metadata?: Record<string, any>;
}

interface FineTunedModels {
  active?: string;       // L'ID du modèle fine-tuné actif
  available?: string[];  // Liste des modèles fine-tunés disponibles pour ce chatbot
  status?: string;       // État du dernier job de fine-tuning
  lastTrainingDate?: string; // Date du dernier fine-tuning
  jobId?: string;        // ID du dernier job de fine-tuning
  dataSources?: string[]; // Les sources de données utilisées pour le dernier fine-tuning
}

interface AISettings {
  apiKey: string;
  prompts: {
    generalPrompt: string;
    attitudePrompt: string;
  };
  knowledgeBase: KnowledgeItem[];
  objectives: string[];
  temperature: number;
  fineTunedModels?: FineTunedModels;
  loading: boolean;
}

const DEFAULT_API_KEY = 'sk-proj-5Dg_5FpbOfhFu8pOt_HEGWFMINXfpfnmA5r6sdPXqbgO2WCJtRHSaIAU5r6sdPXqbgO2WCJtRHSaIAU5mUshrJWYAck5_x0HbT3BlbkFJLfrd1tsGFoWr6KkFkdEGJtQe7iGCA3oV0pyGx-97FsjW5oEQsAdru9KYmxTazESN8K4v_oFtAA';

const DEFAULT_PROMPTS = {
  generalPrompt: 'You are a helpful customer service assistant.',
  attitudePrompt: 'Be concise, friendly, and accurate.'
};

const DEFAULT_TEMPERATURE = 0.7;

interface AISettingsData {
  apiKey?: string;
  prompts?: {
    generalPrompt: string;
    attitudePrompt: string;
  };
  knowledgeBase?: KnowledgeItem[];
  objectives?: string[];
  temperature?: number;
  fineTunedModels?: FineTunedModels;
}

interface AISettingsStore extends AISettings {
  initialized: boolean;
  initializeSettings: (chatbotPublicId: string) => Promise<void>;
  updateAPIKey: (key: string, chatbotPublicId: string) => Promise<void>;
  updatePrompts: (prompts: { generalPrompt: string; attitudePrompt: string }, chatbotPublicId: string) => Promise<void>;
  updateTemperature: (temperature: number, chatbotPublicId: string) => Promise<void>;
  addKnowledge: (item: KnowledgeInput, chatbotPublicId: string) => Promise<void>;
  removeKnowledge: (id: string, chatbotPublicId: string) => Promise<void>;
  addObjective: (objective: string, chatbotPublicId: string) => Promise<void>;
  removeObjective: (index: number, chatbotPublicId: string) => Promise<void>;
  
  // Nouvelles méthodes pour le fine-tuning
  startFineTuning: (chatbotPublicId: string, trainingJob: { fileId: string; model: string; dataSources?: string[] }) => Promise<boolean>;
  updateFineTuningStatus: (status: string, jobId: string, chatbotPublicId: string) => Promise<boolean>;
  addFineTunedModel: (modelId: string, chatbotPublicId: string) => Promise<boolean>;
  activateFineTunedModel: (modelId: string, chatbotPublicId: string) => Promise<boolean>;
  removeFineTunedModel: (modelId: string, chatbotPublicId: string) => Promise<boolean>;
  getFineTunedModels: (chatbotPublicId: string) => Promise<FineTunedModels | undefined>;
}

export const useAISettings = create<AISettingsStore>((set, get) => ({
  apiKey: DEFAULT_API_KEY,
  prompts: DEFAULT_PROMPTS,
  knowledgeBase: [],
  objectives: [],
  temperature: DEFAULT_TEMPERATURE,
  fineTunedModels: undefined,
  loading: false,
  initialized: false,

  initializeSettings: async (chatbotPublicId) => {
    try {
      set({ loading: true });

      const aiSettings = await getChatbotConfigSection<AISettingsData>('ai_settings', chatbotPublicId);
      
      if (aiSettings) {
        set({
          apiKey: aiSettings.apiKey || DEFAULT_API_KEY,
          prompts: aiSettings.prompts || DEFAULT_PROMPTS,
          knowledgeBase: aiSettings.knowledgeBase || [],
          objectives: aiSettings.objectives || [],
          temperature: aiSettings.temperature !== undefined ? aiSettings.temperature : DEFAULT_TEMPERATURE,
          fineTunedModels: aiSettings.fineTunedModels,
          initialized: true,
          loading: false
        });
      } else {
        set({ 
          apiKey: DEFAULT_API_KEY,
          prompts: DEFAULT_PROMPTS,
          knowledgeBase: [],
          objectives: [],
          temperature: DEFAULT_TEMPERATURE,
          fineTunedModels: undefined,
          initialized: true,
          loading: false 
        });
      }
    } catch (err) {
      console.error('Error initializing AI settings:', err);
      set({ 
        apiKey: DEFAULT_API_KEY,
        prompts: DEFAULT_PROMPTS,
        knowledgeBase: [],
        objectives: [],
        temperature: DEFAULT_TEMPERATURE,
        fineTunedModels: undefined,
        initialized: true,
        loading: false 
      });
    }
  },

  updateAPIKey: async (key, chatbotPublicId) => {
    try {
      set({ loading: true });
      
      const currentSettings = {
        apiKey: get().apiKey,
        prompts: get().prompts,
        knowledgeBase: get().knowledgeBase,
        objectives: get().objectives,
        temperature: get().temperature,
        fineTunedModels: get().fineTunedModels
      };
      
      const updatedSettings = {
        ...currentSettings,
        apiKey: key || DEFAULT_API_KEY
      };
      
      const success = await updateChatbotConfigSection('ai_settings', updatedSettings, chatbotPublicId);
      
      if (!success) {
        throw new Error('Failed to update API key');
      }
      
      set({ apiKey: key || DEFAULT_API_KEY, loading: false });
    } catch (err) {
      console.error('Error updating API key:', err);
      set({ loading: false });
      throw err;
    }
  },

  updatePrompts: async (prompts, chatbotPublicId) => {
    try {
      set({ loading: true });
      
      const currentSettings = {
        apiKey: get().apiKey,
        prompts: get().prompts,
        knowledgeBase: get().knowledgeBase,
        objectives: get().objectives,
        temperature: get().temperature,
        fineTunedModels: get().fineTunedModels
      };
      
      const updatedSettings = {
        ...currentSettings,
        prompts: prompts || DEFAULT_PROMPTS
      };
      
      const success = await updateChatbotConfigSection('ai_settings', updatedSettings, chatbotPublicId);
      
      if (!success) {
        throw new Error('Failed to update prompts');
      }
      
      set({ prompts: prompts || DEFAULT_PROMPTS, loading: false });
    } catch (err) {
      console.error('Error updating prompts:', err);
      set({ loading: false });
      throw err;
    }
  },

  updateTemperature: async (temperature, chatbotPublicId) => {
    try {
      set({ loading: true });
      
      const currentSettings = {
        apiKey: get().apiKey,
        prompts: get().prompts,
        knowledgeBase: get().knowledgeBase,
        objectives: get().objectives,
        temperature: get().temperature,
        fineTunedModels: get().fineTunedModels
      };
      
      const updatedSettings = {
        ...currentSettings,
        temperature
      };
      
      const success = await updateChatbotConfigSection('ai_settings', updatedSettings, chatbotPublicId);
      
      if (!success) {
        throw new Error('Failed to update temperature');
      }
      
      set({ temperature, loading: false });
    } catch (err) {
      console.error('Error updating temperature:', err);
      set({ loading: false });
      throw err;
    }
  },

  addKnowledge: async (item, chatbotPublicId) => {
    try {
      set({ loading: true });

      // Current knowledge base
      const knowledgeBase = [...get().knowledgeBase];
      
      // Generate a unique ID
      const id = `kb_${Date.now().toString(36)}_${Math.random().toString(36).substr(2, 5)}`;
      
      // Add new item
      knowledgeBase.push({
        id,
        ...item
      });
      
      // Update settings
      const currentSettings = {
        apiKey: get().apiKey,
        prompts: get().prompts,
        knowledgeBase: get().knowledgeBase,
        objectives: get().objectives,
        temperature: get().temperature,
        fineTunedModels: get().fineTunedModels
      };
      
      const updatedSettings = {
        ...currentSettings,
        knowledgeBase
      };
      
      const success = await updateChatbotConfigSection('ai_settings', updatedSettings, chatbotPublicId);
      
      if (!success) {
        throw new Error('Failed to add knowledge');
      }
      
      set({ knowledgeBase, loading: false });
    } catch (err) {
      console.error('Error adding knowledge:', err);
      set({ loading: false });
      throw err;
    }
  },

  removeKnowledge: async (id, chatbotPublicId) => {
    try {
      set({ loading: true });
      
      // Filter out the item to remove
      const knowledgeBase = get().knowledgeBase.filter(item => item.id !== id);
      
      // Update settings
      const currentSettings = {
        apiKey: get().apiKey,
        prompts: get().prompts,
        knowledgeBase: get().knowledgeBase,
        objectives: get().objectives,
        temperature: get().temperature,
        fineTunedModels: get().fineTunedModels
      };
      
      const updatedSettings = {
        ...currentSettings,
        knowledgeBase
      };
      
      const success = await updateChatbotConfigSection('ai_settings', updatedSettings, chatbotPublicId);
      
      if (!success) {
        throw new Error('Failed to remove knowledge');
      }
      
      set({ knowledgeBase, loading: false });
    } catch (err) {
      console.error('Error removing knowledge:', err);
      set({ loading: false });
      throw err;
    }
  },

  addObjective: async (objective, chatbotPublicId) => {
    try {
      set({ loading: true });
      
      // Current objectives
      const objectives = [...get().objectives, objective];
      
      // Update settings
      const currentSettings = {
        apiKey: get().apiKey,
        prompts: get().prompts,
        knowledgeBase: get().knowledgeBase,
        objectives: get().objectives,
        temperature: get().temperature,
        fineTunedModels: get().fineTunedModels
      };
      
      const updatedSettings = {
        ...currentSettings,
        objectives
      };
      
      const success = await updateChatbotConfigSection('ai_settings', updatedSettings, chatbotPublicId);
      
      if (!success) {
        throw new Error('Failed to add objective');
      }
      
      set({ objectives, loading: false });
    } catch (err) {
      console.error('Error adding objective:', err);
      set({ loading: false });
      throw err;
    }
  },

  removeObjective: async (index, chatbotPublicId) => {
    try {
      set({ loading: true });
      
      // Remove objective at index
      const objectives = get().objectives.filter((_, i) => i !== index);
      
      // Update settings
      const currentSettings = {
        apiKey: get().apiKey,
        prompts: get().prompts,
        knowledgeBase: get().knowledgeBase,
        objectives: get().objectives,
        temperature: get().temperature,
        fineTunedModels: get().fineTunedModels
      };
      
      const updatedSettings = {
        ...currentSettings,
        objectives
      };
      
      const success = await updateChatbotConfigSection('ai_settings', updatedSettings, chatbotPublicId);
      
      if (!success) {
        throw new Error('Failed to remove objective');
      }
      
      set({ objectives, loading: false });
    } catch (err) {
      console.error('Error removing objective:', err);
      set({ loading: false });
      throw err;
    }
  },

  // Méthodes pour le fine-tuning
  startFineTuning: async (chatbotPublicId, trainingJob) => {
    try {
      set({ loading: true });
      
      // Mettre à jour les informations de fine-tuning
      const currentSettings = {
        apiKey: get().apiKey,
        prompts: get().prompts,
        knowledgeBase: get().knowledgeBase,
        objectives: get().objectives,
        temperature: get().temperature,
        fineTunedModels: get().fineTunedModels || {}
      };
      
      const updatedFineTunedModels = {
        ...currentSettings.fineTunedModels,
        status: 'pending',
        lastTrainingDate: new Date().toISOString(),
        jobId: trainingJob.fileId,
        dataSources: trainingJob.dataSources || [] // Stockage des sources sélectionnées
      };
      
      const updatedSettings = {
        ...currentSettings,
        fineTunedModels: updatedFineTunedModels
      };
      
      const success = await updateChatbotConfigSection('ai_settings', updatedSettings, chatbotPublicId);
      
      if (!success) {
        throw new Error('Failed to start fine-tuning');
      }
      
      set({ 
        fineTunedModels: updatedFineTunedModels as FineTunedModels,
        loading: false 
      });
      
      return true;
    } catch (err) {
      console.error('Error starting fine-tuning:', err);
      set({ loading: false });
      return false;
    }
  },
  
  updateFineTuningStatus: async (status, jobId, chatbotPublicId) => {
    try {
      set({ loading: true });
      
      // Mettre à jour le statut du job de fine-tuning
      const currentSettings = {
        apiKey: get().apiKey,
        prompts: get().prompts,
        knowledgeBase: get().knowledgeBase,
        objectives: get().objectives,
        temperature: get().temperature,
        fineTunedModels: get().fineTunedModels || {}
      };
      
      const updatedFineTunedModels = {
        ...currentSettings.fineTunedModels,
        status,
        jobId
      };
      
      const updatedSettings = {
        ...currentSettings,
        fineTunedModels: updatedFineTunedModels
      };
      
      const success = await updateChatbotConfigSection('ai_settings', updatedSettings, chatbotPublicId);
      
      if (!success) {
        throw new Error('Failed to update fine-tuning status');
      }
      
      set({ 
        fineTunedModels: updatedFineTunedModels as FineTunedModels,
        loading: false 
      });
      
      return true;
    } catch (err) {
      console.error('Error updating fine-tuning status:', err);
      set({ loading: false });
      return false;
    }
  },
  
  addFineTunedModel: async (modelId, chatbotPublicId) => {
    try {
      set({ loading: true });
      
      // Ajouter un nouveau modèle fine-tuné à la liste des modèles disponibles
      const currentSettings = {
        apiKey: get().apiKey,
        prompts: get().prompts,
        knowledgeBase: get().knowledgeBase,
        objectives: get().objectives,
        temperature: get().temperature,
        fineTunedModels: get().fineTunedModels || {}
      };
      
      const currentAvailable = currentSettings.fineTunedModels.available || [];
      
      // Vérifier si le modèle existe déjà
      if (!currentAvailable.includes(modelId)) {
        const updatedFineTunedModels = {
          ...currentSettings.fineTunedModels,
          available: [...currentAvailable, modelId],
          // Si c'est le premier modèle, l'activer automatiquement
          active: currentAvailable.length === 0 ? modelId : currentSettings.fineTunedModels.active
        };
        
        const updatedSettings = {
          ...currentSettings,
          fineTunedModels: updatedFineTunedModels
        };
        
        const success = await updateChatbotConfigSection('ai_settings', updatedSettings, chatbotPublicId);
        
        if (!success) {
          throw new Error('Failed to add fine-tuned model');
        }
        
        set({ 
          fineTunedModels: updatedFineTunedModels as FineTunedModels,
          loading: false 
        });
      } else {
        set({ loading: false });
      }
      
      return true;
    } catch (err) {
      console.error('Error adding fine-tuned model:', err);
      set({ loading: false });
      return false;
    }
  },
  
  activateFineTunedModel: async (modelId, chatbotPublicId) => {
    try {
      set({ loading: true });
      
      // Activer un modèle fine-tuné spécifique
      const currentSettings = {
        apiKey: get().apiKey,
        prompts: get().prompts,
        knowledgeBase: get().knowledgeBase,
        objectives: get().objectives,
        temperature: get().temperature,
        fineTunedModels: get().fineTunedModels || {}
      };
      
      // Pour 'none', nous désactivons simplement le fine-tuning
      if (modelId === 'none') {
        const updatedFineTunedModels = {
          ...currentSettings.fineTunedModels,
          active: undefined
        };
        
        const updatedSettings = {
          ...currentSettings,
          fineTunedModels: updatedFineTunedModels
        };
        
        const success = await updateChatbotConfigSection('ai_settings', updatedSettings, chatbotPublicId);
        
        if (!success) {
          throw new Error('Failed to deactivate fine-tuned model');
        }
        
        set({ 
          fineTunedModels: updatedFineTunedModels as FineTunedModels,
          loading: false 
        });
        
        return true;
      }
      
      // Vérifier si le modèle existe dans la liste des modèles disponibles
      let currentAvailable = currentSettings.fineTunedModels.available || [];
      let needsToAddModel = false;
      
      // Si le modèle n'existe pas dans la liste des disponibles, l'ajouter
      if (!currentAvailable.includes(modelId)) {
        console.log(`Model ${modelId} not found in available models, adding it`);
        currentAvailable = [...currentAvailable, modelId];
        needsToAddModel = true;
      }
      
      // Mettre à jour les modèles fine-tunés
      const updatedFineTunedModels = {
        ...currentSettings.fineTunedModels,
        available: currentAvailable,
        active: modelId
      };
      
      const updatedSettings = {
        ...currentSettings,
        fineTunedModels: updatedFineTunedModels
      };
      
      const success = await updateChatbotConfigSection('ai_settings', updatedSettings, chatbotPublicId);
      
      if (!success) {
        throw new Error('Failed to activate fine-tuned model');
      }
      
      set({ 
        fineTunedModels: updatedFineTunedModels as FineTunedModels,
        loading: false 
      });
      
      return true;
    } catch (err) {
      console.error('Error activating fine-tuned model:', err);
      set({ loading: false });
      return false;
    }
  },
  
  removeFineTunedModel: async (modelId, chatbotPublicId) => {
    try {
      set({ loading: true });
      
      // Supprimer un modèle fine-tuné de la liste des modèles disponibles
      const currentSettings = {
        apiKey: get().apiKey,
        prompts: get().prompts,
        knowledgeBase: get().knowledgeBase,
        objectives: get().objectives,
        temperature: get().temperature,
        fineTunedModels: get().fineTunedModels || {}
      };
      
      const currentAvailable = currentSettings.fineTunedModels.available || [];
      
      // Supprimer le modèle de la liste
      const updatedAvailable = currentAvailable.filter(id => id !== modelId);
      
      // Si le modèle actif est supprimé, désactiver le modèle actif
      const active = currentSettings.fineTunedModels.active === modelId 
        ? undefined 
        : currentSettings.fineTunedModels.active;
      
      const updatedFineTunedModels = {
        ...currentSettings.fineTunedModels,
        available: updatedAvailable,
        active
      };
      
      const updatedSettings = {
        ...currentSettings,
        fineTunedModels: updatedFineTunedModels
      };
      
      const success = await updateChatbotConfigSection('ai_settings', updatedSettings, chatbotPublicId);
      
      if (!success) {
        throw new Error('Failed to remove fine-tuned model');
      }
      
      set({ 
        fineTunedModels: updatedFineTunedModels as FineTunedModels,
        loading: false 
      });
      
      return true;
    } catch (err) {
      console.error('Error removing fine-tuned model:', err);
      set({ loading: false });
      return false;
    }
  },
  
  getFineTunedModels: async (chatbotPublicId) => {
    try {
      // Récupérer la configuration actuelle
      const aiSettings = await getChatbotConfigSection<AISettingsData>('ai_settings', chatbotPublicId);
      return aiSettings?.fineTunedModels;
    } catch (err) {
      console.error('Error getting fine-tuned models:', err);
      return undefined;
    }
  }
}));